import { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { Button } from '@domcrm/embed-ui';

import { postRequestPasswordReset } from '../widget-api/auth-password-reset';
import { ApiError } from '../widget-api/http-client';
import { useWidgetSeo } from '../widget-hooks/use-widget-seo';
import { PATH } from '../widget-lib/path-routes';
import { validateEmail } from '../widget-lib/validation';
import { EmbedMessageBlock } from '../widget-ui/embed-message-block';
import { PromoBanner } from '../widget-ui/promo-banner';
import { useSmartCaptcha } from '../widget-lib/smartcaptcha';
import { SmartCaptchaNotice } from '../widget-ui/smart-captcha-notice';

const inputClass =
  'min-h-10 w-full rounded-sm border-0 bg-[var(--bg-soft)] px-3 py-2 text-base text-[var(--text)] focus-visible:ring-2 focus-visible:ring-[var(--focus-ring)] sm:min-h-8 sm:py-1 sm:text-[13px]';

export default function ForgotPasswordPage() {
  useWidgetSeo(
    'Восстановление пароля',
    'Инструкция по сбросу и восстановлению пароля личного кабинета.'
  );
  const [searchParams] = useSearchParams();
  const qs = searchParams.toString();
  const suffix = qs ? `?${qs}` : '';

  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** После успешного POST форма скрывается, показывается только текст и навигация. */
  const [submitted, setSubmitted] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Yandex SmartCaptcha (невидимая): включается бэкендом (SMARTCAPTCHA_MODE=required).
  const captcha = useSmartCaptcha();

  const canSubmit = useMemo(() => {
    if (loading) return false;
    return validateEmail(email, { required: true }) === true;
  }, [email, loading]);

  async function onSubmit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    if (!canSubmit) return;
    setLoading(true);
    setError(null);
    try {
      const res = await captcha.run((captchaToken) =>
        postRequestPasswordReset({
          email: email.trim().toLowerCase(),
          ...(captchaToken ? { captcha_token: captchaToken } : {})
        })
      );
      const text = ((res.detail ?? res.message) ?? '').trim();
      setSuccessMessage(
        text ||
          'Если этот адрес зарегистрирован, мы отправили на него письмо со ссылкой для сброса пароля.'
      );
      setSubmitted(true);
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : 'Не удалось отправить запрос.';
      setError(msg);
      if (captcha.enabled) captcha.reset();
    } finally {
      setLoading(false);
    }
  }

  if (submitted) {
    return (
      <div className="mx-auto w-full max-w-md space-y-4 w-full min-w-0">
        <div>
          <h1 className="text-xl font-semibold tracking-tight">Ссылка отправлена</h1>
          <EmbedMessageBlock variant="success" size="field" className="w-full" role="status" aria-live="polite">
            {successMessage ?? ''}
          </EmbedMessageBlock>
        </div>
        <div className="flex flex-col gap-3 border-t border-[var(--border)]/60 pt-4 text-center text-sm sm:flex-row sm:justify-center sm:gap-6">
          <Link className="font-medium text-[var(--accent)] underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--focus-ring)]" to={`${PATH.home}${suffix}`}>
            На главную
          </Link>
          <Link className="text-[var(--text-muted)] underline-offset-2 hover:underline" to={`${PATH.signIn}${suffix}`}>
            Назад ко входу
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-md space-y-4 w-full min-w-0">
      <div>
        <h1 className="text-xl font-semibold tracking-tight">Забыли пароль</h1>
        <PromoBanner screen="auth" />
        <p className="mt-1 text-sm leading-relaxed text-[var(--text-muted)]">
          Укажите email учётной записи. Если адрес зарегистрирован, мы отправим ссылку для сброса. Повторный запрос — не чаще
          одного раза в 5 минут.
        </p>
      </div>
      <form className="space-y-4" onSubmit={(e) => void onSubmit(e)} noValidate>
        <div>
          <label className="mb-1.5 block text-[13px] font-medium text-[var(--text)]" htmlFor="forgot-email">
            Email
          </label>
          <input
            id="forgot-email"
            type="email"
            autoComplete="email"
            className={inputClass}
            value={email}
            onChange={(ev) => setEmail(ev.target.value)}
          />
        </div>
        <p className="text-xs leading-relaxed text-[var(--text-muted)]">
          Если указанный email зарегистрирован в системе, мы отправим инструкцию по сбросу пароля.
        </p>
        {error ? (
          <EmbedMessageBlock variant="error" size="field" className="w-full" role="alert" aria-live="assertive">
            {error}
          </EmbedMessageBlock>
        ) : null}
        <SmartCaptchaNotice
          serverEnabled={captcha.serverEnabled}
          challengeVisible={captcha.challengeVisible}
          status={captcha.status}
          onRetry={captcha.refresh}
        />
        <Button type="submit" className="w-full min-h-11 sm:min-h-9" disabled={!canSubmit || loading || captcha.loading}>
          {loading ? 'Отправляем…' : 'Отправить ссылку'}
        </Button>
      </form>
      <p className="text-center text-sm">
        <Link className="text-[var(--accent)] underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--focus-ring)]" to={`${PATH.signIn}${suffix}`}>
          Назад ко входу
        </Link>
      </p>
    </div>
  );
}
